<template>
  <b-col md="12" class="mb-3">
    <b-card class="formCardDash">
      <div class="card-header">
        <h6 class="card-title mb-0">Profit & Loss</h6>
        <small class="text-muted">Total Profit: {{ formatCurrency(totalProfit) }} | Total Records: {{ totalRecords }}</small>
      </div>
      <div class="card-body">
        <!-- Filters -->
        <b-row class="mb-3">
          <b-col md="3">
            <b-form-input
              v-model="searchQuery"
              placeholder="Search by job no, billing party..."
              @input="debounceSearch"
              size="sm"
            />
          </b-col>
          <b-col md="2">
            <b-form-select
              v-model="perPage"
              :options="[10, 25, 50, 100]"
              @change="updatePagination"
              size="sm"
            />
          </b-col>
          <b-col md="2">
            <b-button
              variant="outline-info"
              size="sm"
              @click="refreshData"
              :disabled="isLoading"
            >
              <i class="fa fa-refresh" :class="{ 'fa-spin': isLoading }"></i>
              Refresh
            </b-button>
          </b-col>
        </b-row>

        <!-- Bootstrap Table with Pagination -->
        <b-table
          :items="profitLossData"
          :fields="fields"
          :busy="isLoading"
          striped
          bordered
          hover
          small
          show-empty
          empty-text="No profit & loss data found"
          class="mb-0"
        >
          <template slot="table-busy">
            <div class="text-center text-danger my-2">
              <b-spinner class="align-middle" small></b-spinner>
              <strong class="ml-2">Loading...</strong>
            </div>
          </template>

          <template slot="pl_job_no" slot-scope="data">
            <code>{{ data.value }}</code>
          </template>

          <template slot="pl_billing_party" slot-scope="data">
            <strong>{{ data.value }}</strong>
          </template>

          <template slot="pl_expenses" slot-scope="data">
            <span class="text-danger">{{ formatCurrency(data.value) }}</span>
          </template>

          <template slot="pl_incomes" slot-scope="data">
            <span class="text-success">{{ formatCurrency(data.value) }}</span>
          </template>

          <template slot="pl_profit" slot-scope="data">
            <strong :class="data.value >= 0 ? 'text-success' : 'text-danger'">
              {{ formatCurrency(data.value) }}
            </strong>
          </template>

          <template slot="actions" slot-scope="data">
            <b-button
              size="sm"
              variant="outline-info"
              @click.stop="toggleDetails(data.item)"
              class="toggle-btn"
            >
              <i :class="data.item._showDetails ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
            </b-button>
          </template>

          <!-- Row Details Slot -->
          <template slot="row-details" slot-scope="row">
            <div class="row-details">
              <!-- Job Details -->
              <div class="row mb-3" v-if="row.item.jobDetails">
                <div class="col-12">
                  <div class="card border-success">
                    <div class="card-header bg-success text-white">
                      <i class="fa fa-briefcase"></i> Job Details
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-3">
                          <strong>Job No:</strong> {{ row.item.jobDetails.JobNo }}
                        </div>
                        <div class="col-md-3">
                          <strong>Job Date:</strong> {{ formatDate(row.item.jobDetails.JobDate) }}
                        </div>
                        <div class="col-md-3">
                          <strong>Job Type:</strong> {{ row.item.jobDetails.JobType }}
                        </div>
                        <div class="col-md-3">
                          <strong>Transport Type:</strong> {{ row.item.jobDetails.JobTransportType }}
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-md-3">
                          <strong>Client:</strong> {{ row.item.jobDetails.ClientName }}
                        </div>
                        <div class="col-md-3">
                          <strong>Shipping Line:</strong> {{ row.item.jobDetails.SlineName }}
                        </div>
                        <div class="col-md-3">
                          <strong>Raised By:</strong> {{ row.item.jobDetails.RaisedBy }}
                        </div>
                        <div class="col-md-3">
                          <strong>Raised From:</strong> {{ row.item.jobDetails.RaisedFrom }}
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-md-3">
                          <strong>POL → POD:</strong> {{ row.item.jobDetails.POL }} → {{ row.item.jobDetails.POD }}
                        </div>
                        <div class="col-md-3">
                          <strong>Container Type:</strong> {{ row.item.jobDetails.JobContainerType }}
                        </div>
                        <div class="col-md-3">
                          <strong>Enquiry:</strong> {{ row.item.jobDetails.EnqNo }}
                        </div>
                        <div class="col-md-3">
                          <strong>SOB:</strong> {{ row.item.jobDetails.SOB }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- P&L Summary -->
              <div class="row mb-3">
                <div class="col-12">
                  <div class="card border-primary">
                    <div class="card-header bg-primary text-white">
                      <i class="fa fa-chart-line"></i> P&L Summary
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-3">
                          <strong>P&L ID:</strong> {{ row.item.pl_id }}
                        </div>
                        <div class="col-md-3">
                          <strong>Job No:</strong> {{ row.item.pl_job_no }}
                        </div>
                        <div class="col-md-3">
                          <strong>Billing Party:</strong> {{ row.item.pl_billing_party }}
                        </div>
                        <div class="col-md-3">
                          <strong>Employee:</strong> {{ row.item.pl_cu_relation.employeeName || 'N/A' }}
                        </div>
                      </div>
                      <div class="row mt-3">
                        <div class="col-md-4">
                          <div class="text-center">
                            <h5 class="text-danger">{{ formatCurrency(row.item.pl_expenses) }}</h5>
                            <small class="text-muted">Total Expenses</small>
                          </div>
                        </div>
                        <div class="col-md-4">
                          <div class="text-center">
                            <h5 class="text-success">{{ formatCurrency(row.item.pl_incomes) }}</h5>
                            <small class="text-muted">Total Income</small>
                          </div>
                        </div>
                        <div class="col-md-4">
                          <div class="text-center">
                            <h5 :class="row.item.pl_profit >= 0 ? 'text-success' : 'text-danger'">
                              {{ formatCurrency(row.item.pl_profit) }}
                            </h5>
                            <small class="text-muted">Net Profit/Loss</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </b-table>

        <!-- Pagination -->
        <b-pagination
          v-if="totalItems > perPage"
          limit="25"
          align="center"
          v-model="currentPage"
          :per-page="perPage"
          :total-rows="totalItems"
          @change="onPageChange"
          class="mt-3"
        ></b-pagination>

        <!-- Table Info -->
        <div class="mt-2 d-flex justify-content-between align-items-center">
          <div>
            <b-form-select
              v-model="pagination.perPage"
              :options="pagination.pageOptions"
              @change="updatePagination"
              size="sm"
              class="w-auto"
            ></b-form-select>
            <span class="ml-2 text-muted">per page</span>
          </div>

          <div class="text-muted small">
            Total Records: {{ totalItems }} | Total Profit: {{ formatCurrency(totalProfit) }}
          </div>
        </div>

      </div>
    </b-card>
  </b-col>
</template>

<script>
import moment from "moment";
import SalesPersonTeusService from "../../../services/SalesPersonTeusService";

export default {
  name: "ProfitLossSection",
  props: {
    profitLossData: {
      type: Array,
      required: true,
      default: () => [],
    },
    paginationData: {
      type: Object,
      default: () => ({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10
      }),
    },
    summaryData: {
      type: Object,
      default: () => ({
        totalRevenue: 0,
        totalExpenses: 0,
        netProfit: 0,
        profitMargin: 0
      }),
    },
  },
  data() {
    return {
      searchQuery: '',
      searchTimeout: null,
      isLoading: false,
      totalItems: 0,
      totalPages: 1,
      currentPage: 1,
      perPage: 10,
      fields: [
        { key: 'pl_job_no', label: 'Job No', sortable: true },
        { key: 'pl_billing_party', label: 'Billing Party', sortable: true },
        { key: 'pl_expenses', label: 'Expenses', sortable: true, class: 'text-right' },
        { key: 'pl_incomes', label: 'Income', sortable: true, class: 'text-right' },
        { key: 'pl_profit', label: 'Profit/Loss', sortable: true, class: 'text-right' },
        { key: 'actions', label: 'Actions', sortable: false, class: 'text-center' }
      ]
    };
  },
  computed: {
    totalProfit() {
      return this.profitLossData.reduce((sum, item) => sum + (item.pl_profit || 0), 0);
    },

    totalRecords() {
      return this.totalItems;
    },
  },
  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) return "₹0";
      return "₹" + Number(value).toLocaleString("en-IN");
    },

    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD-MM-YYYY");
    },

    toggleDetails(item) {
      // Toggle the _showDetails property for BootstrapVue row-details
      item._showDetails = !item._showDetails;
      this.$forceUpdate(); // Force update to trigger reactivity
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.filter = this.searchQuery;
      }, 500);
    },

    updatePagination() {
      this.currentPage = 1;
    },

    onFiltered(filteredItems) {
      // Trigger pagination reset when filtered
      this.currentPage = 1;
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadData();
    },

    async loadData() {
      this.isLoading = true;
      try {
        const payload = {
          salesPersonId: this.$store.state.user.userId,
          clientId: this.$store.state.client.clientId || null,
          dateRange: this.$store.state.dateRange || '2025-2026',
          page: this.currentPage,
          limit: this.perPage,
          search: this.searchQuery
        };

        // Emit event to parent to load profit loss data
        this.$emit('load-profit-loss-data', payload);
      } catch (error) {
        console.error('Error loading profit loss data:', error);
        this.isLoading = false;
      }
    },


    refreshData() {
      this.loadData();
    },
  },

  mounted() {
    this.loadData();
  },

  watch: {
    profitLossData: {
      handler(newData) {
        // Initialize _showDetails property for each item
        if (newData && newData.length > 0) {
          newData.forEach(item => {
            if (item._showDetails === undefined) {
              this.$set(item, '_showDetails', false);
            }
          });
        }
      },
      immediate: true,
      deep: true
    },
    paginationData: {
      handler(newPagination) {
        this.totalItems = (newPagination && newPagination.totalItems) || 0;
        this.totalPages = (newPagination && newPagination.totalPages) || 1;
      },
      immediate: true,
      deep: true
    }
  }
};
</script>

<style scoped>
.formCardDash {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.expanded-details {
  background-color: #f8f9fa;
  border-top: 2px solid #dee2e6;
  padding: 15px;
}

.expanded-details .row {
  margin-bottom: 10px;
}

.expanded-details h6 {
  margin-bottom: 10px;
  font-weight: 600;
}

.expanded-details table.table-sm {
  font-size: 12px;
}

.expanded-details table.table-sm td {
  padding: 4px 8px;
  border: none;
}

.table th {
  position: sticky;
  top: 0;
  background-color: #007bff !important;
  color: white !important;
  z-index: 10;
  border-color: #0056b3;
  font-weight: 600;
  text-align: center;
  vertical-align: middle;
  padding: 12px 8px;
}

.table thead th {
  border-bottom: 2px solid #0056b3;
}

.table tbody td {
  vertical-align: middle;
  padding: 8px;
}

.table tbody td.text-center {
  text-align: center;
}

.table tbody td.text-right {
  text-align: right;
}

.badge {
  font-size: 11px;
}

.btn-outline-info {
  border-color: #17a2b8;
  color: #17a2b8;
}

.btn-outline-info:hover {
  background-color: #17a2b8;
  border-color: #17a2b8;
}

.toggle-btn {
  min-width: 40px;
  transition: all 0.2s ease;
}

.toggle-btn:hover {
  transform: scale(1.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .expanded-details .col-md-6 {
    margin-bottom: 20px;
  }

  .table-responsive {
    max-height: 400px;
  }
}
</style>


// WEBPACK FOOTER //
// ProfitLossSection.vue?5ce9d282